import * as Dialog from '@radix-ui/react-dialog';
import { Command, defaultFilter } from 'cmdk';
import { useCallback, useMemo } from 'react';
import { keyboardLayoutSearchVariants } from './keyboard-layout';

export interface CommandPaletteItem {
  label: string;
  value: string;
  onSelect: () => void;
  /** Приоритет группы результатов: меньшее значение — выше (меню = 0, документация = 1). */
  rank?: number;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandPaletteItem[];
  /** Подпись поля поиска. */
  searchPlaceholder?: string;
  /** Текст при пустом результате фильтра. */
  emptyLabel?: string;
  dataTestId?: string;
  /** Управляемое значение поиска — например, чтобы искать сущности на сервере по мере ввода. */
  search?: string;
  onSearchChange?: (search: string) => void;
}

export function CommandPalette({
  open,
  onOpenChange,
  items,
  searchPlaceholder = 'Команды, разделы, сущности…',
  emptyLabel = 'Ничего не найдено',
  dataTestId,
  search,
  onSearchChange
}: CommandPaletteProps) {
  const rankByValue = useMemo(() => new Map(items.map((item) => [item.value, item.rank ?? 0])), [items]);
  const maxRank = useMemo(() => items.reduce((max, item) => Math.max(max, item.rank ?? 0), 0), [items]);

  /**
   * Ранжирование cmdk: поиск понимает обе раскладки клавиатуры, а найденные
   * элементы получают бонус группы, чтобы разделы меню всегда были выше документации.
   */
  const filter = useCallback(
    (value: string, search: string, keywords?: string[]) => {
      let best = 0;
      for (const variant of keyboardLayoutSearchVariants(search)) {
        best = Math.max(best, defaultFilter(value, variant, keywords));
      }
      if (best <= 0) return 0;
      return best + (maxRank - (rankByValue.get(value) ?? 0));
    },
    [rankByValue, maxRank]
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[5600] bg-black/40" />
        <Dialog.Content
          data-testid={dataTestId}
          aria-describedby={undefined}
          className="fixed left-1/2 top-24 z-[5601] w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 rounded-[var(--ui-radius)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-2 shadow-xl outline-none"
        >
          <Dialog.Title className="sr-only">Поиск</Dialog.Title>
          <Command className="bg-transparent" filter={filter}>
            <Command.Input
              {...(search !== undefined ? { value: search } : {})}
              {...(onSearchChange ? { onValueChange: onSearchChange } : {})}
              className="h-10 w-full bg-transparent px-3 text-sm text-[hsl(var(--ui-foreground))] outline-none placeholder:text-[hsl(var(--ui-muted-foreground))]"
              placeholder={searchPlaceholder}
            />
            <Command.List className="max-h-80 overflow-auto py-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-[hsl(var(--ui-muted-foreground))]">{emptyLabel}</Command.Empty>
              {items.map((item) => (
                <Command.Item
                  key={item.value}
                  value={item.value}
                  onSelect={item.onSelect}
                  className="cursor-pointer rounded-[var(--ui-radius-control)] px-3 py-2 text-sm aria-selected:bg-[hsl(var(--ui-muted))] aria-selected:text-[hsl(var(--ui-foreground))]"
                >
                  {item.label}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
